import classNames from 'classnames';
import React, { InputHTMLAttributes, PropsWithChildren, useId, useRef } from 'react';

/**
 * The backoffice's building blocks, in the home's style: a card per section, labelled fields that line
 * up, one look for inputs and selects, and a file picker that says which file is picked — the browser's own
 * shows its controls in the system's language.
 */

export const inputClassName =
  'h-10 rounded-lg border-0 bg-dl-surface px-3 text-base text-dl-text ring-1 ring-dl-border placeholder:text-dl-faint focus:outline-none focus:ring-2 focus:ring-dl-primary disabled:bg-dl-sunken disabled:text-dl-faint';

export const selectClassName = classNames(inputClassName, 'pr-8');

/** A section of a page: a card, its title, an optional line of explanation, and actions on the right. */
export function Card({
  title,
  description,
  actions,
  className,
  children,
  id,
}: PropsWithChildren<{
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  id?: string;
}>) {
  const generated = useId();
  const titleId = id ?? `card-${generated}`;
  return (
    <section
      aria-labelledby={title ? titleId : undefined}
      className={classNames('rounded-2xl border border-dl-border bg-dl-surface p-6', className)}
    >
      {(title || actions) && (
        <div className="mb-4 flex flex-wrap items-end gap-4">
          <div className="mr-auto min-w-0">
            {title && (
              <h3
                id={titleId}
                className="text-xl font-semibold text-dl-text"
              >
                {title}
              </h3>
            )}
            {description && <p className="mt-1 text-base text-dl-muted">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-end gap-3">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** A labelled control, its hint underneath. */
export function Field({
  label,
  hint,
  className,
  children,
}: PropsWithChildren<{ label: React.ReactNode; hint?: React.ReactNode; className?: string }>) {
  return (
    // the hint stays outside the label: the control's name is the label's text alone
    <div className={classNames('flex flex-col gap-1', className)}>
      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-dl-muted">{label}</span>
        {children}
      </label>
      {hint && <span className="text-sm text-dl-faint">{hint}</span>}
    </div>
  );
}

/**
 * A file input dressed as a button, followed by the picked file's name. Give it an `aria-label`: inside a
 * label, the button's and the name's text would otherwise become part of its accessible name.
 */
export function FilePicker({
  file,
  label = 'Choose a file…',
  inputRef,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  file: File | null;
  label?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  const own = useRef<HTMLInputElement>(null);
  const ref = (inputRef as React.RefObject<HTMLInputElement>) ?? own;
  return (
    <span className="flex h-10 items-center gap-3">
      <input
        ref={ref}
        type="file"
        className="sr-only"
        {...props}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="h-10 shrink-0 rounded-lg border border-dl-border bg-dl-surface px-4 text-base font-medium text-dl-text transition hover:border-dl-strong hover:bg-dl-raised"
      >
        {label}
      </button>
      <span
        className={classNames('min-w-0 truncate text-base', file ? 'text-dl-text' : 'text-dl-faint')}
        title={file?.name}
      >
        {file ? file.name : 'No file chosen'}
      </span>
    </span>
  );
}

/** Previous / next under a paged table, and where we are. */
export function Pager({
  page,
  pages,
  total,
  noun,
  onPage,
}: {
  page: number;
  pages: number;
  total: number;
  noun: string;
  onPage: (page: number) => void;
}) {
  return (
    <div className="flex items-center gap-2 pt-4 text-base tabular-nums text-dl-muted">
      <span className="mr-auto">
        Page {page + 1} of {Math.max(1, pages)} ({total} {noun})
      </span>
      <PagerButton
        aria-label="Previous page"
        onClick={() => onPage(Math.max(0, page - 1))}
        disabled={page === 0}
      >
        ← Previous
      </PagerButton>
      <PagerButton
        aria-label="Next page"
        onClick={() => onPage(page + 1)}
        disabled={page + 1 >= pages}
      >
        Next →
      </PagerButton>
    </div>
  );
}

function PagerButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className="rounded-lg border border-dl-border bg-dl-surface px-4 py-1.5 font-medium text-dl-text transition hover:border-dl-strong hover:bg-dl-raised disabled:cursor-default disabled:text-dl-faint disabled:hover:border-dl-border disabled:hover:bg-dl-surface"
      {...props}
    />
  );
}
